import React, { useState } from "react";
import { View, StyleSheet } from "react-native";
import { Trash, ChatCircle, CaretDown, CaretUp } from "@phosphor-icons/react";
import { useCreatePostMutation, useDeletePostMutation } from "../../../graphql/generated/graphql";

import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import TextEditor from "../../../components/TextEditor/TextEditor";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import CustomHtml from "../../../components/common/general/CustomHtml/CustomHtml";
import DeleteResource from "../../../components/content/DeleteResource/DeleteResource";
import { PaperPlane } from "@phosphor-icons/react/dist/ssr/PaperPlane";

interface Post {
  id: string;
  message: string;
  depth: number;
  threadId: string;
  created: string;
  lastModified: string;
  author: {
    id: string;
    name: string;
  };
  replies: Post[];
  replyTo?: {
    id: string;
    message: string;
    author: {
      id: string;
      name: string;
    };
  };
}

interface AdminPostResponsesContainerProps {
  posts: Post[];
  selectedThread: {
    id: string;
    title: string;
    [key: string]: unknown;
  } | null;
  currentUser: {
    id: string;
    name: string;
    role: string;
  };
  replyingTo: string | null;
  setReplyingTo: React.Dispatch<React.SetStateAction<string | null>>;
  replyContent: string;
  setReplyContent: React.Dispatch<React.SetStateAction<string>>;
  expandedReplies: Set<string>;
  setExpandedReplies: React.Dispatch<React.SetStateAction<Set<string>>>;
  isReplySubmitting: boolean;
  setIsReplySubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  refetch?: () => Promise<void>;
}

const AdminPostResponsesContainer: React.FC<AdminPostResponsesContainerProps> = ({
  posts,
  selectedThread,
  currentUser,
  replyingTo,
  setReplyingTo,
  replyContent,
  setReplyContent,
  expandedReplies,
  setExpandedReplies,
  isReplySubmitting,
  setIsReplySubmitting,
  refetch,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingPost, setDeletingPost] = useState<{ id: string; message: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // GraphQL mutations
  const [createPostMutation] = useCreatePostMutation();
  const [deletePostMutation] = useDeletePostMutation();

  // Filter to only show depth 1 posts (direct responses to the initial post)
  const depth1Posts = posts.filter((post) => post.depth === 1);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const canDelete = (post: Post) => {
    // Admin/faculty can delete any post, students can only delete their own
    return deletePostMutation && (currentUser.role === "faculty" || post.author.id === currentUser.id);
  };

  const isFacultyPost = (post: Post) => {
    console.log("Checking if post is faculty:", post);
    // Check if post author is faculty - in real implementation, check user role
    return post.author.name.includes("Dr.") || post.author.id === "user-faculty";
  };

  const handleReply = (postId: string) => {
    setReplyingTo(postId);
    setReplyContent("");
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyContent("");
  };

  const handleSubmitReply = async (parentPostId: string) => {
    if (!replyContent.trim() || !selectedThread) return;

    setIsReplySubmitting(true);

    try {
      const result = await createPostMutation({
        variables: {
          input: {
            message: replyContent.trim(),
            threadId: selectedThread.id,
            replyToPostId: parentPostId,
          },
        },
      });

      if (!result.data?.createPost?.success) {
        throw new Error(result.data?.createPost?.message || "Failed to create reply");
      }

      // Clear reply form
      setReplyingTo(null);
      setReplyContent("");

      // Refetch posts to show the new reply
      if (refetch) {
        await refetch();
      }
    } catch (error) {
      // Could add error handling/notification here
    } finally {
      setIsReplySubmitting(false);
    }
  };

  const handleDeletePost = (postId: string, message: string) => {
    setDeletingPost({ id: postId, message });
    setShowDeleteModal(true);
  };

  const confirmDeletePost = async () => {
    if (!deletingPost || !deletePostMutation) return;

    setIsDeleting(true);

    try {
      const result = await deletePostMutation({
        variables: {
          postId: deletingPost.id,
        },
      });

      if (!result.data?.deletePost?.success) {
        throw new Error(result.data?.deletePost?.message || "Failed to delete post");
      }

      setShowDeleteModal(false);
      setDeletingPost(null);

      // Refetch posts to show the deletion
      if (refetch) {
        await refetch();
      }
    } catch (error) {
      // Could add error handling/notification here
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingPost(null);
  };

  const toggleReplies = (postId: string) => {
    const newExpanded = new Set(expandedReplies);
    if (newExpanded.has(postId)) {
      newExpanded.delete(postId);
    } else {
      newExpanded.add(postId);
    }
    setExpandedReplies(newExpanded);
  };

  const renderReplyForm = (parentPostId: string) => (
    <View style={styles.replyFormContainer}>
      <View style={styles.replyFormHeader}>
        <CustomText text="Replying to " size="s" style={styles.replyingToText} useTranslationText={false} />
        <CustomText
          text={depth1Posts.find((p) => p.id === parentPostId)?.author.name || ""}
          size="s"
          weight="medium"
          style={styles.replyingToName}
          useTranslationText={false}
        />
      </View>

      <CustomSpacing type="vertical" size="s" />

      <View style={styles.replyEditorContainer}>
        <TextEditor
          value={replyContent}
          onChange={setReplyContent}
          placeholder="Type your reply..."
          disabled={isReplySubmitting}
        />
      </View>

      <CustomSpacing type="vertical" size="s" />

      <View style={styles.replyButtonContainer}>
        <CustomButton
          text="Post Reply"
          size="s"
          styleType="primary"
          disabled={isReplySubmitting || !replyContent.trim()}
          onPress={() => handleSubmitReply(parentPostId)}
          useTranslationText={false}
          leftIcon={<PaperPlane size={16} color="#FFFFFF" weight="bold" />}
          bold={true}
        />
        <CustomButton
          text="Cancel"
          size="s"
          styleType="light"
          disabled={isReplySubmitting}
          onPress={handleCancelReply}
          useTranslationText={false}
          bold={true}
        />
      </View>
    </View>
  );

  const renderPost = (post: Post) => {
    // Get depth 2 replies for this post from the global posts array
    const depth2Replies = posts.filter((p) => p.depth === 2 && p.replyTo?.id === post.id);

    return (
      <View key={post.id} style={styles.postContainer}>
        <View
          style={[
            styles.postCard,
            isFacultyPost(post) && styles.facultyPostCard,
            replyingTo === post.id && styles.replyingToPostCard,
          ]}>
          {/* Post Header */}
          <View style={styles.postHeader}>
            <View style={styles.authorInfo}>
              <CustomText
                text={post.author.name}
                size="s"
                weight="medium"
                style={styles.authorName}
                useTranslationText={false}
              />
              {isFacultyPost(post) && (
                <View style={styles.instructorBadge}>
                  <CustomText
                    text="INSTRUCTOR"
                    size="xs"
                    weight="medium"
                    style={styles.instructorText}
                    useTranslationText={false}
                  />
                </View>
              )}
            </View>
            <CustomText text={formatDate(post.created)} size="xs" style={styles.postDate} useTranslationText={false} />
            {canDelete(post) && (
              <Trash size={16} color="#6B7280" onClick={() => handleDeletePost(post.id, post.message)} />
            )}
          </View>

          <CustomSpacing type="vertical" size="xs" />

          {/* Post Content */}
          <CustomHtml html={post.message} textStyle={styles.postContent} />

          <CustomSpacing type="vertical" size="s" />

          {/* Post Actions */}
          <View style={styles.postActions}>
            <CustomButton
              text="Reply"
              size="s"
              styleType="light"
              onPress={() => handleReply(post.id)}
              useTranslationText={false}
              leftIcon={<ChatCircle size={16} color="#6B7280" weight="regular" />}
              textStyle={{ color: "#6B7280" }}
            />

            {depth2Replies.length > 0 && (
              <>
                <CustomSpacing type="horizontal" size="s" />
                <CustomButton
                  text={`${depth2Replies.length} ${depth2Replies.length === 1 ? "reply" : "replies"}`}
                  size="s"
                  styleType="light"
                  onPress={() => toggleReplies(post.id)}
                  useTranslationText={false}
                  leftIcon={<ChatCircle size={16} color="#6B7280" weight="regular" />}
                  rightIcon={
                    expandedReplies.has(post.id) ? (
                      <CaretUp size={16} color="#6B7280" weight="regular" />
                    ) : (
                      <CaretDown size={16} color="#6B7280" weight="regular" />
                    )
                  }
                  textStyle={{ color: "#6B7280" }}
                />
              </>
            )}
          </View>

          {/* Render replies if expanded */}
          {expandedReplies.has(post.id) && depth2Replies.length > 0 && (
            <View style={styles.repliesContainer}>
              {depth2Replies.map((reply) => (
                <View key={reply.id} style={[styles.replyCard, isFacultyPost(reply) && styles.facultyReplyCard]}>
                  <View style={styles.replyHeader}>
                    <View style={styles.authorInfo}>
                      <CustomText
                        text={reply.author.name}
                        size="xs"
                        weight="medium"
                        style={styles.authorName}
                        useTranslationText={false}
                      />
                      {isFacultyPost(reply) && (
                        <View style={styles.instructorBadge}>
                          <CustomText
                            text="INSTRUCTOR"
                            size="xs"
                            weight="medium"
                            style={styles.instructorText}
                            useTranslationText={false}
                          />
                        </View>
                      )}
                    </View>
                    <CustomText
                      text={formatDate(reply.created)}
                      size="xs"
                      style={styles.postDate}
                      useTranslationText={false}
                    />
                    {canDelete(reply) && (
                      <Trash size={14} color="#6B7280" onClick={() => handleDeletePost(reply.id, reply.message)} />
                    )}
                  </View>

                  <CustomSpacing type="vertical" size="xs" />

                  <CustomHtml html={reply.message} textStyle={styles.postContent} />
                </View>
              ))}
            </View>
          )}

          {/* Reply form if this post is being replied to */}
          {replyingTo === post.id && renderReplyForm(post.id)}
        </View>
      </View>
    );
  };

  return (
    <>
      <View style={styles.section}>
        <CustomText
          text="Responses"
          size="l"
          weight="semibold"
          style={styles.sectionTitle}
          useTranslationText={false}
        />

        <CustomSpacing type="vertical" size="m" />

        {depth1Posts.length === 0 ? (
          <View style={styles.emptyState}>
            <CustomText
              text="No responses yet"
              size="m"
              weight="medium"
              style={styles.emptyTitle}
              useTranslationText={false}
            />
            <CustomText
              text="This discussion hasn't received any responses from students yet."
              size="s"
              style={styles.emptyDescription}
              useTranslationText={false}
            />
          </View>
        ) : (
          <View>{depth1Posts.map(renderPost)}</View>
        )}
      </View>

      <CustomModal
        visible={showDeleteModal}
        onRequestClose={handleCancelDelete}
        ariaLabel="Delete Post Confirmation"
        showCloseButton={false}>
        <DeleteResource
          type="post"
          deleteButtonClick={confirmDeletePost}
          backButtonClick={handleCancelDelete}
          isDeleting={isDeleting}
        />
      </CustomModal>
    </>
  );
};

const styles = StyleSheet.create({
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    color: "#101828",
  },
  postContainer: {
    marginBottom: 16,
  },
  postCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 20,
  },
  facultyPostCard: {
    borderLeftWidth: 4,
    borderLeftColor: "#3B82F6",
  },
  replyingToPostCard: {
    backgroundColor: "#F0F9FF",
    borderColor: "#3B82F6",
  },
  postHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  replyHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  authorInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  authorName: {
    color: "#101828",
  },
  instructorBadge: {
    backgroundColor: "#3B82F6",
    borderRadius: 4,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  instructorText: {
    color: "#FFFFFF",
  },
  postDate: {
    color: "#6B7280",
  },
  postContent: {
    color: "#374151",
    lineHeight: 24,
  },
  postActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  repliesContainer: {
    marginTop: 16,
    paddingLeft: 16,
    borderLeftWidth: 2,
    borderLeftColor: "#E5E7EB",
  },
  replyCard: {
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 16,
    marginBottom: 8,
  },
  facultyReplyCard: {
    borderLeftWidth: 4,
    borderLeftColor: "#3B82F6",
  },
  replyFormContainer: {
    marginTop: 16,
    padding: 16,
    backgroundColor: "#F9FAFB",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E5E7EB",
  },
  replyFormHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  replyingToText: {
    color: "#6B7280",
  },
  replyingToName: {
    color: "#3E68FE",
  },
  replyEditorContainer: {
    minHeight: 80,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    overflow: "hidden",
    backgroundColor: "#FFFFFF",
  },
  replyButtonContainer: {
    flexDirection: "row",
    gap: 8,
  },
  emptyState: {
    padding: 40,
    alignItems: "center",
    backgroundColor: "#F9FAFB",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderStyle: "dashed",
  },
  emptyTitle: {
    color: "#101828",
  },
  emptyDescription: {
    color: "#6B7280",
    marginTop: 8,
  },
});

export default AdminPostResponsesContainer;
